// React
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import moment from "moment";

// MUI
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel, 
    OutlinedInput,
    TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

const PREFIX = "UpdateAdd";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
    switch: `${PREFIX}-switch`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
        "&.Mui-disabled": {
            color: "#fff",
            borderColor: "#ffcdd2",
            background: "#ffcdd2",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
    [`& .${classes.switch}`]: {
        "& .MuiSwitch-switchBase .MuiSwitch-thumb": {
            color: "#FFF",
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: "#031851",
        },
        "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
            color: "#FF5968",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#FFA8B1",
        },
    },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "rgba(19, 31, 62, 0.80)",
        fontSize: 12,
        maxWidth: 320,
        borderRadius: 6,
        fontWeight: "normal",
    },
});

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

export const UpdateAdd = (props) => {
    // Properties
    const { addHourFee, isError, set, setalertType, setShow, setTexto, sumTotal, values, isErrorFocus, setIsErrorFocus } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component functions */
    const handleChange = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
        });
        setIsErrorFocus(false)
    };

    const handleChangeHoursBook = (event) => {
        let bookMonths = 0;
        if (values.additionalsOnly) {
            if (values.fixedHours) {
                bookMonths = 1
            } else {
                bookMonths = isNaN(moment(values.finishDateBook).diff(moment(values.startDateBook)))
                ? 0
                : Math.ceil(moment(values.finishDateBook).diff(moment(values.startDateBook), "months", true));
            }
        }
        set({
            ...values,
            [event.target.name]: event.target.value,
            projectTotal: values.hourRate * parseFloat(event.target.value) * bookMonths * (1 - values.discount / 100),
        });
        setIsErrorFocus(false)
    };

    const handleChangeCheck = (event, value) => {
        switch (value) {
            case 0:
                set({
                    ...values,
                    checkedAR: event.target.checked,
                    tothAR: 0,
                    startDateAR: event.target.checked ? values.sinceDate : null,
                    finishDateAR: event.target.checked ? values.untilDate : null,
                });
                break;
            case 1:
                set({
                    ...values,
                    checkedAP: event.target.checked,
                    tothAP: 0,
                    startDateAP: event.target.checked ? values.sinceDate : null,
                    finishDateAP: event.target.checked ? values.untilDate : null,
                });
                break;
            case 2:
                set({
                    ...values,
                    checkedAccrual: event.target.checked,
                    tothAccrual: 0,
                    startDateAccrual: event.target.checked ? values.sinceDate : null,
                    finishDateAccrual: event.target.checked ? values.untilDate : null,
                });
                break;
            case 3:
                set({
                    ...values,
                    checkedClass: event.target.checked,
                    tothClass: 0,
                    startDateClass: event.target.checked ? values.sinceDate : null,
                    finishDateClass: event.target.checked ? values.untilDate : null,
                });
                break;
            case 4:
                set({
                    ...values,
                    checkedMulti: event.target.checked,
                    tothMulti: 0,
                    startDateMulti: event.target.checked ? values.sinceDate : null,
                    finishDateMulti: event.target.checked ? values.untilDate : null,
                });
                break;
            case 5:
                set({
                    ...values,
                    checkedPer: event.target.checked,
                    tothPer: 0,
                    startDatePer: event.target.checked ? values.sinceDate : null,
                    finishDatePer: event.target.checked ? values.untilDate : null,
                });
                break;
            case 6:
                set({
                    ...values,
                    checkedOthers: event.target.checked,
                    nameOthers: "",
                    tothOthers: 0,
                    startDateOthers: event.target.checked ? values.sinceDate : null,
                    finishDateOthers: event.target.checked ? values.untilDate : null,
                });
                break;
            case 7:
                set({
                    ...values,
                    checkedChecks: event.target.checked,
                    tothChecks: 0,
                    startDateChecks: event.target.checked ? values.sinceDate : null,
                    finishDateChecks: event.target.checked ? values.untilDate : null,
                });
                break;
            default:
                let meses = Math.ceil(values.untilDate.diff(values.sinceDate, "months", true));

                set({
                    ...values,
                    checkedBook: event.target.checked,
                    tothBook: 0,
                    startDateBook: event.target.checked ? values.sinceDate : null,
                    finishDateBook: event.target.checked ? values.untilDate : null,
                    projectTotal: 0,
                    months: event.target.checked ? meses : 1,
                });
                break;
        }
        setIsErrorFocus(false)
    };

    const handleChangeStartDateAdd = (date, val) => {
        let startDate = moment(date).startOf("month");
        switch (val) {
            case 0:
                if (!date || !values.finishDateAR) {
                    set({
                        ...values,
                        startDateAR: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAR))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAR: startDate,
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.finishDateAP) {
                    set({
                        ...values,
                        startDateAP: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAP))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAP: startDate,
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.finishDateAccrual) {
                    set({
                        ...values,
                        startDateAccrual: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAccrual))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAccrual: startDate,
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.finishDateClass) {
                    set({
                        ...values,
                        startDateClass: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateClass))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateClass: startDate,
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.finishDateMulti) {
                    set({
                        ...values,
                        startDateMulti: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateMulti))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateMulti: startDate,
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.finishDatePer) {
                    set({
                        ...values,
                        startDatePer: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDatePer))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDatePer: startDate,
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.finishDateOthers) {
                    set({
                        ...values,
                        startDateOthers: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOthers))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOthers: startDate,
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.finishDateChecks) {
                    set({
                        ...values,
                        startDateChecks: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateChecks))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateChecks: startDate,
                        });
                    }
                }
                break;
            default:
                let bookMonths = 0;
                if (values.additionalsOnly) {
                    if (values.fixedHours) {
                        bookMonths = 1
                    } else {
                        bookMonths = isNaN(moment(values.finishDateBook).diff(startDate))
                        ? 0
                        : Math.ceil(moment(values.finishDateBook).diff(startDate, "months", true));
                    }
                }

                if (!date || !values.finishDateBook) {
                    set({
                        ...values,
                        startDateBook: date ? startDate : date,
                        projectTotal:
                            values.hourRate * parseFloat(values.tothBook) * bookMonths * (1 - values.discount / 100),
                        months: values.additionalsOnly ? bookMonths : values.months,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateBook))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateBook: startDate,
                            projectTotal:
                                values.hourRate *
                                parseFloat(values.tothBook) *
                                bookMonths *
                                (1 - values.discount / 100),
                            months: values.additionalsOnly ? bookMonths : values.months,
                        });
                    }
                }
                break;
        }
        setIsErrorFocus(false)
    };

    const handleChangeEndDateAdd = (date, val) => {
        let endDate = moment(date).endOf("month").startOf("day");
        switch (val) {
            case 0:
                if (!date || !values.startDateAR) {
                    set({
                        ...values,
                        finishDateAR: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAR))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAR: endDate,
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.startDateAP) {
                    set({
                        ...values,
                        finishDateAP: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAP))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAP: endDate,
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.startDateAccrual) {
                    set({
                        ...values,
                        finishDateAccrual: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAccrual))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAccrual: endDate,
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.startDateClass) {
                    set({
                        ...values,
                        finishDateClass: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateClass))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateClass: endDate,
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.startDateMulti) {
                    set({
                        ...values,
                        finishDateMulti: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateMulti))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateMulti: endDate,
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.startDatePer) {
                    set({
                        ...values,
                        finishDatePer: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDatePer))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDatePer: endDate,
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.startDateOthers) {
                    set({
                        ...values,
                        finishDateOthers: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOthers))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOthers: endDate,
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.startDateChecks) {
                    set({
                        ...values,
                        finishDateChecks: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateChecks))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateChecks: endDate,
                        });
                    }
                }
                break;
            default:
                let bookMonths = 0;
                if (values.additionalsOnly) {
                    if (values.fixedHours) {
                        bookMonths = 1
                    } else {
                        bookMonths = isNaN(endDate.diff(moment(values.startDateBook)))
                        ? 0
                        : Math.ceil(endDate.diff(moment(values.startDateBook), "months", true));
                    }
                }

                if (!date || !values.startDateBook) {
                    set({
                        ...values,
                        finishDateBook: date ? endDate : date,
                        projectTotal:
                            values.hourRate * parseFloat(values.tothBook) * bookMonths * (1 - values.discount / 100),
                        months: values.additionalsOnly ? bookMonths : values.months,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateBook))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateBook: endDate,
                            projectTotal:
                                values.hourRate *
                                parseFloat(values.tothBook) *
                                bookMonths *
                                (1 - values.discount / 100),
                            months: values.additionalsOnly ? bookMonths : values.months,
                        });
                    }
                }
                break;
        }
        setIsErrorFocus(false)
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0} sx={{ backgroundColor: "inherit" }}>
                    <CardHeader title={t("proposals.additionals")} />
                    <Divider />
                    <CardContent sx={{ pb: 5 }}>
                        <Grid container spacing={2} alignItems="center">
                            {values.additionalsOnly && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedBook"
                                                    checked={values.checkedBook}
                                                    onChange={() => {
                                                        return false;
                                                    }}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                />
                                            }
                                            label="Bookkeeper"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedBook}
                                            label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                            name="tothBook"
                                            onChange={handleChangeHoursBook}
                                            value={values.tothBook}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={
                                                values.checkedBook &&
                                                isError &&
                                                (values.tothBook === 0 || values.tothBook === "0")
                                            }
                                            onClick={(e) =>{setIsErrorFocus(false)}}                                            
                                            inputRef={(input) => { (input && isErrorFocus && values.checkedBook &&
                                                isError &&
                                                (values.tothBook === 0 || values.tothBook === "0")) && input.focus()}}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedBook}
                                            value={values.startDateBook}
                                            minDate={moment(values.sinceDate).startOf("month")}
                                            maxDate={moment(values.untilDate).endOf("month")}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 8)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedBook}
                                                    {...params}
                                                    error={values.checkedBook && isError && !values.startDateBook}
                                                />
                                            )}
                                            // inputProps={{ readOnly: true }}
                                            inputProps={{
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input && isErrorFocus && values.checkedBook && isError && !values.startDateBook) && input.focus()}}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedBook}
                                            value={values.finishDateBook}
                                            minDate={moment(values.sinceDate).startOf("month")}
                                            maxDate={moment(values.untilDate).endOf("month")}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 8)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedBook}
                                                    {...params}
                                                    error={values.checkedBook && isError && !values.finishDateBook}
                                                />
                                            )}
                                            // inputProps={{ readOnly: true }}
                                            inputProps={{
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input && isErrorFocus && values.checkedBook && isError && !values.finishDateBook) && input.focus()}}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.checkBen")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedChecks"
                                                checked={values.checkedChecks}
                                                onChange={(value) => handleChangeCheck(value, 7)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("proposals.checks")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedChecks}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothChecks"
                                    onChange={handleChange}
                                    value={values.tothChecks}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedChecks &&
                                        isError &&
                                        (values.tothChecks === 0 || values.tothChecks === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedChecks &&
                                        isError &&
                                        (values.tothChecks === 0 || values.tothChecks === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: {
                                            actions: ["clear"],
                                        },
                                    }}
                                    disabled={!values.checkedChecks}
                                    value={values.startDateChecks}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 7)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedChecks}
                                            {...params}
                                            error={values.checkedChecks && isError && !values.startDateChecks}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedChecks && isError && !values.startDateChecks) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: {
                                            actions: ["clear"],
                                        },
                                    }}
                                    disabled={!values.checkedChecks}
                                    value={values.finishDateChecks}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 7)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedChecks}
                                            {...params}
                                            error={values.checkedChecks && isError && !values.finishDateChecks}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedChecks && isError && !values.finishDateChecks) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.regAnali")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedAR"
                                                checked={values.checkedAR}
                                                onChange={(value) => handleChangeCheck(value, 0)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("proposals.arAdd")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedAR}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothAR"
                                    onChange={handleChange}
                                    value={values.tothAR}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedAR && isError && (values.tothAR === 0 || values.tothAR === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAR && isError && (values.tothAR === 0 || values.tothAR === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAR}
                                    value={values.startDateAR}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 0)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.startDateAR}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAR && isError && !values.startDateAR) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAR}
                                    value={values.finishDateAR}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 0)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.finishDateAR}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAR && isError && !values.finishDateAR) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.addAPAcc")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedAP"
                                                checked={values.checkedAP}
                                                onChange={(value) => handleChangeCheck(value, 1)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("proposals.apAdd")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedAP}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothAP"
                                    onChange={handleChange}
                                    value={values.tothAP}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedAP && isError && (values.tothAP === 0 || values.tothAP === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAP && isError && (values.tothAP === 0 || values.tothAP === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAP}
                                    value={values.startDateAP}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 1)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.startDateAP}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAP && isError && !values.startDateAP) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAP}
                                    value={values.finishDateAP}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 1)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.finishDateAP}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAP && isError && !values.finishDateAP) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("services.accDet")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedAccrual"
                                                checked={values.checkedAccrual}
                                                onChange={(value) => handleChangeCheck(value, 2)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("services.accrualAccounting")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedAccrual}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothAccrual"
                                    onChange={handleChange}
                                    value={values.tothAccrual}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedAccrual &&
                                        isError &&
                                        (values.tothAccrual === 0 || values.tothAccrual === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAccrual &&
                                        isError &&
                                        (values.tothAccrual === 0 || values.tothAccrual === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAccrual}
                                    value={values.startDateAccrual}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 2)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAccrual}
                                            {...params}
                                            error={values.checkedAccrual && isError && !values.startDateAccrual}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAccrual && isError && !values.startDateAccrual) && input.focus()}}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAccrual}
                                    value={values.finishDateAccrual}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 2)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAccrual}
                                            {...params}
                                            error={values.checkedAccrual && isError && !values.finishDateAccrual}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedAccrual && isError && !values.finishDateAccrual) && input.focus()}}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.addCostsCent")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedClass"
                                                checked={values.checkedClass}
                                                onChange={(value) => handleChangeCheck(value, 3)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("services.classesCategories")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedClass}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothClass"
                                    onChange={handleChange}
                                    value={values.tothClass}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedClass &&
                                        isError &&
                                        (values.tothClass === 0 || values.tothClass === "0")
                                    }

                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedClass &&
                                        isError &&
                                        (values.tothClass === 0 || values.tothClass === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedClass}
                                    value={values.startDateClass}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 3)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.startDateClass}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedClass && isError && !values.startDateClass) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedClass}
                                    value={values.finishDateClass}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 3)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.finishDateClass}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedClass && isError && !values.finishDateClass) && input.focus()}}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.addRecordRe")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedMulti"
                                                checked={values.checkedMulti}
                                                onChange={(value) => handleChangeCheck(value, 4)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("proposals.multiEntity")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedMulti}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothMulti"
                                    onChange={handleChange}
                                    value={values.tothMulti}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedMulti &&
                                        isError &&
                                        (values.tothMulti === 0 || values.tothMulti === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedMulti &&
                                        isError &&
                                        (values.tothMulti === 0 || values.tothMulti === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedMulti}
                                    value={values.startDateMulti}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 4)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.startDateMulti}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedMulti && isError && !values.startDateMulti) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedMulti}
                                    value={values.finishDateMulti}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 4)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.finishDateMulti}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedMulti && isError && !values.finishDateMulti) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LightTooltip title={t("proposals.addTayMade")}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="checkedPer"
                                                checked={values.checkedPer}
                                                onChange={(value) => handleChangeCheck(value, 5)}
                                                inputProps={{
                                                    "aria-label": "controlled",
                                                }}
                                                disabled={!values.sinceDate || !values.untilDate}
                                            />
                                        }
                                        label={t("services.personalizedReporting")}
                                    />
                                </LightTooltip>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedPer}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothPer"
                                    onChange={handleChange}
                                    value={values.tothPer}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedPer && isError && (values.tothPer === 0 || values.tothPer === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus && values.checkedPer && isError && (values.tothPer === 0 || values.tothPer === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedPer}
                                    value={values.startDatePer}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 5)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.startDatePer}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus &&values.checkedPer && isError && !values.startDatePer) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedPer}
                                    value={values.finishDatePer}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 5)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.finishDatePer}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus &&values.checkedPer && isError && !values.finishDatePer) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3} xl={2} sx={{ paddingLeft: "5px !important" }}>
                                        <Checkbox
                                            name="checkedOthers"
                                            checked={values.checkedOthers}
                                            onChange={(value) => handleChangeCheck(value, 6)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.sinceDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={9} xl={10}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedOthers}
                                            label={t("services.others")}
                                            name="nameOthers"
                                            onChange={handleChange}
                                            value={values.nameOthers}
                                            variant="outlined"
                                            required={values.checkedOthers}
                                            error={values.checkedOthers && isError && values.nameOthers === ""}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                            inputRef={(input) => { (input && isErrorFocus &&values.checkedOthers && isError && values.nameOthers === "") && input.focus()}}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedOthers}
                                    label={values.fixedHours ? t("miscellaneous.hours") : t("miscellaneous.hoursMonth")}
                                    name="tothOthers"
                                    onChange={handleChange}
                                    value={values.tothOthers}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={
                                        values.checkedOthers &&
                                        isError &&
                                        (values.tothOthers === 0 || values.tothOthers === "0")
                                    }
                                    onClick={(e) =>{setIsErrorFocus(false)}}                                    
                                    inputRef={(input) => { (input && isErrorFocus &&values.checkedOthers &&
                                        isError &&
                                        (values.tothOthers === 0 || values.tothOthers === "0")) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.startDateOthers}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 6)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.startDateOthers}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus &&values.checkedOthers && isError && !values.startDateOthers) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.finishDateOthers}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 6)}
                                    minDate={moment(values.sinceDate).startOf("month")}
                                    maxDate={moment(values.untilDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.finishDateOthers}
                                            onClick={(e) =>{setIsErrorFocus(false)}}
                                        />
                                    )}
                                    // inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                    inputProps={{
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    onClick={(e) =>{setIsErrorFocus(false)}}
                                    inputRef={(input) => { (input && isErrorFocus &&values.checkedOthers && isError && !values.finishDateOthers) && input.focus()}}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}></Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label={t("services.hoursTotal")}
                                    value={sumTotal}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRate">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRate"
                                        value={addHourFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRateAdd">
                                        {t("services.hourlyRateAdd")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRateAdd"
                                        value={addHourFee * sumTotal}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRateAdd")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
